// 🔧 promotion_idea_service.js
// ✅ Purpose: Business service for Promotion Engine ideas
// ✅ Features:
// - Creates manual Promotion Engine ideas
// - Loads ideas from Firestore
// - Updates idea lifecycle status
// - Keeps page components away from Firestore implementation details
// - Supports MVP 1 internal LGV marketing ideas
// - Preserves MVP 2 event/organization-ready fields
// - Fully block-commented for clarity and future devs

import {
  addDoc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";

import {
  getPromotionIdeaRef,
  getPromotionIdeasCollection,
} from "./promotion_repository";

import {
  PROMOTION_ENGINE_SOURCES,
  PROMOTION_SCOPES,
} from "../config/promotion_settings_map";

import {
  PROMOTION_IDEA_STATUS,
} from "../constants/promotion_status";

/**
 * createPromotionIdea
 *
 * Creates a new idea record in the Promotion Engine Idea Bank.
 *
 * MVP 1:
 * - Used by LGV admins to manually add internal LGV marketing ideas.
 *
 * MVP 2:
 * - Same shape can support event-level ideas from managers,
 *   sponsors, vendors, volunteers, auctions, passport, etc.
 */
export const createPromotionIdea = async ({
  title,
  description = "",
  aiPrompt = "",
  category = "",
  platforms = [],
  postTypes = [],
  tags = [],
  notes = "",
  createdBy = "",
  scope = PROMOTION_SCOPES.INTERNAL_LGV,
  eventId = null,
  organizationId = null,
  engineSource = PROMOTION_ENGINE_SOURCES.MANUAL,
}) => {
  /**
   * Basic guardrail so empty idea records are not created.
   */
  if (!title || !title.trim()) {
    throw new Error("Idea title is required.");
  }

  const ideaPayload = {
    title: title.trim(),

    description: description.trim(),
    ...(aiPrompt.trim() ? { aiPrompt: aiPrompt.trim() } : {}),

    category,

    platforms,

    postTypes,

    tags,

    notes: notes.trim(),

    scope,

    eventId,

    organizationId,

    engineSource,

    ideaStatus: PROMOTION_IDEA_STATUS.NEW,

    aiGenerated: false,

    createdBy,

    updatedBy: createdBy,

    createdAt: serverTimestamp(),

    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(
    getPromotionIdeasCollection(),
    ideaPayload
  );

  return {
    id: docRef.id,
    ...ideaPayload,
  };
};

/**
 * getPromotionIdeas
 *
 * Loads Promotion Engine ideas.
 *
 * Later phases can add Firestore query filters.
 * For Phase 2, we keep this simple and stable.
 */
export const getPromotionIdeas = async ({ eventId = null, pageSize = 100 } = {}) => {
  const scopeFilter = eventId
    ? where("eventId", "==", eventId)
    : where("scope", "==", PROMOTION_SCOPES.INTERNAL_LGV);
  const ideasQuery = query(
    getPromotionIdeasCollection(),
    scopeFilter,
    orderBy("updatedAt", "desc"),
    limit(pageSize)
  );

  const snapshot = await getDocs(ideasQuery);

  return snapshot.docs.map((ideaDoc) => ({
    id: ideaDoc.id,
    ...ideaDoc.data(),
  }));
};

export const getPromotionIdea = async (ideaId) => {
  if (!ideaId) throw new Error("Idea ID is required.");

  const snapshot = await getDoc(getPromotionIdeaRef(ideaId));
  if (!snapshot.exists()) throw new Error("Promotion idea not found.");

  return { id: snapshot.id, ...snapshot.data() };
};

// Pin an existing source/legacy Idea independently of social content. A live
// transaction preserves shared context when two workspaces save concurrently.
export const savePromotionIdeaContext = async ({ ideaId, aiPrompt, eventId = null }) => {
  if (!ideaId) throw new Error("Idea ID is required.");
  if (typeof aiPrompt !== "string" || !aiPrompt.trim()) throw new Error("AI Prompt / Idea is required.");
  return runTransaction(db, async (transaction) => {
    const reference = getPromotionIdeaRef(ideaId);
    const snapshot = await transaction.get(reference);
    if (!snapshot.exists()) throw new Error("Promotion idea not found.");
    const idea = snapshot.data();
    if ((idea.eventId || null) !== (eventId || null)) throw new Error("This idea belongs to another event.");
    const savedPrompt = typeof idea.aiPrompt === "string" ? idea.aiPrompt.trim() : "";
    if (savedPrompt && savedPrompt !== aiPrompt.trim()) throw new Error("AI Prompt / Idea is locked after saving. Create another Idea to change its context.");
    if (!savedPrompt) transaction.update(reference, { aiPrompt: aiPrompt.trim(), updatedAt: serverTimestamp() });
    return { ...idea, id: snapshot.id || ideaId, aiPrompt: savedPrompt ? idea.aiPrompt : aiPrompt.trim() };
  });
};

/**
 * updatePromotionIdeaStatus
 *
 * Updates only the idea lifecycle status.
 *
 * This will be used later for:
 * - Archive
 * - Ready for AI
 * - Converted to queue
 */
export const updatePromotionIdeaStatus = async ({
  ideaId,
  ideaStatus,
  updatedBy = "",
}) => {
  if (!ideaId) {
    throw new Error("Idea ID is required.");
  }

  if (!ideaStatus) {
    throw new Error("Idea status is required.");
  }

  await updateDoc(
    getPromotionIdeaRef(ideaId),
    {
      ideaStatus,
      updatedBy,
      updatedAt: serverTimestamp(),
    }
  );

  return true;
};
